import {makeObservable, observable, runInAction} from 'mobx';

import type {BNCoachInvitesSendParams} from '../types/bnrks/methods';
import type {
  CoreClientInvitesBindParams,
  CoreClientInvitesMarkReadParams,
  CoreClientInvitesReadParams,
  CoreCoachInvitesDeleteParams,
  CoreCoachInvitesReadParams,
  CoreCoachInvitesSendParams,
  CoreCoachInvitesSendToCourseParams,
  CoreCoachInvitesSendToProgramParams,
  CorePublicInvitesReadParams,
} from '../types/core/methods';
import type {MethodStatus} from '../utils/entityStore';
import type {InviteApiModel} from './types';

import {pickCoreOrB2b} from '../../utils/coreOrB2b/pickCoreOrB2b';
import {envManager} from '../../utils/env';
import {AppError} from '../../utils/errors/app';
import {apiRequest} from '../utils/apiRequest';
import {EntityStore} from '../utils/entityStore';

export class InviteStore<
  T extends InviteApiModel = InviteApiModel,
> extends EntityStore<T, object, object, object, CoreCoachInvitesDeleteParams> {
  client = new EntityStore<T, CoreClientInvitesReadParams>('invite', {
    fetch: 'client.invites.read',
  });
  coach = new EntityStore<T, CoreCoachInvitesReadParams>('invite', {
    fetch: 'coach.invites.read',
  });
  markRead = this._createMethod<CoreClientInvitesMarkReadParams>({
    method: 'client.invites.mark_read',
  });
  public = new EntityStore<T, CorePublicInvitesReadParams>('invite', {
    fetch: 'public.invites.read',
  });
  sending: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  sendingToCourse: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };
  sendingToProgram: MethodStatus<T> = {
    entity: null,
    error: null,
    inProgress: false,
    success: false,
  };

  constructor(...args: ConstructorParameters<typeof EntityStore>) {
    super(...args);

    makeObservable(this, {
      sending: observable,
      sendingToProgram: observable,
    });
  }

  async bind(params?: CoreClientInvitesBindParams) {
    const result = await apiRequest<CoreClientInvitesBindParams>({
      method: 'client.invites.bind',
      params,
    });

    return result.invite as T;
  }

  async send({
    b2b,
    core,
  }: {
    b2b?: BNCoachInvitesSendParams;
    core?: CoreCoachInvitesSendParams;
  } = {}) {
    try {
      runInAction(() => {
        this.sending = {
          entity: null,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const result = await apiRequest<CoreCoachInvitesSendParams>({
        method: 'coach.invites.send',
        params: pickCoreOrB2b({
          b2b,
          core,
        }),
      });

      runInAction(() => {
        this.sending = {
          entity: null,
          error: null,
          inProgress: false,
          success: true,
        };
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.sending = {
          entity: null,
          error,
          inProgress: false,
          success: false,
        };
      });

      throw error;
    }
  }

  async sendToCourse(params?: CoreCoachInvitesSendToCourseParams) {
    if (envManager.isB2b) {
      throw new AppError('Method is not allowed for B2B');
    }

    try {
      runInAction(() => {
        this.sendingToCourse = {
          entity: null,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const result = await apiRequest<CoreCoachInvitesSendToCourseParams>({
        method: 'coach.invites.send_to_course',
        params,
      });

      runInAction(() => {
        this.sendingToCourse = {
          entity: null,
          error: null,
          inProgress: false,
          success: true,
        };
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.sendingToCourse = {
          entity: null,
          error,
          inProgress: false,
          success: false,
        };
      });

      throw error;
    }
  }

  async sendToProgram(params?: CoreCoachInvitesSendToProgramParams) {
    if (envManager.isB2b) {
      throw new AppError('Method is not allowed for B2B');
    }

    try {
      runInAction(() => {
        this.sendingToProgram = {
          entity: null,
          error: null,
          inProgress: true,
          success: false,
        };
      });

      const result = await apiRequest<CoreCoachInvitesSendToProgramParams>({
        method: 'coach.invites.send_to_program',
        params,
      });

      runInAction(() => {
        this.sendingToProgram = {
          entity: null,
          error: null,
          inProgress: false,
          success: true,
        };
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.sendingToProgram = {
          entity: null,
          error,
          inProgress: false,
          success: false,
        };
      });

      throw error;
    }
  }
}

export const inviteStore = new InviteStore('invite', {
  delete: 'coach.invites.delete',
});
